import React, { useState } from 'react';
import api from '../../api';

const STATUS_STYLES = {
  approved: { backgroundColor: '#d4edda', color: '#155724' },
  pending: { backgroundColor: '#fff3cd', color: '#856404' },
  pending_review: { backgroundColor: '#cce5ff', color: '#004085' },
  rejected: { backgroundColor: '#f8d7da', color: '#721c24' },
  cancelled: { backgroundColor: '#f8d7da', color: '#721c24' },
};

const STATUS_LABELS = {
  approved: 'Aprobado',
  pending: 'Pendiente',
  pending_review: 'Pendiente revisión',
  rejected: 'Rechazado',
  cancelled: 'Cancelado',
};

const formatComment = (payment) => {
  const comentario = payment.comentario?.trim();
  if (!comentario) return null;
  const detalle = payment.comentario_detalle?.trim();
  return detalle ? `${comentario}: ${detalle}` : comentario;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return '$' + Number(amount).toLocaleString('es-AR', { minimumFractionDigits: 0 });
};

const PaymentsTable = ({ payments, events = [], onRefresh }) => {
  const [reviewingId, setReviewingId] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [filter, setFilter] = useState('all');

  // Los pagos se vinculan al evento por título (misma clave que usa el backend).
  const commentColor = (payment) => {
    const comentario = payment.comentario?.trim().toLowerCase();
    if (!comentario) return null;
    const event = events.find((e) => e.title === payment.title);
    const option = event?.comment_options?.find(
      (o) => o.label?.trim().toLowerCase() === comentario
    );
    return option?.sheet_color || null;
  };

  const handleReview = async (paymentId, newStatus) => {
    if (!window.confirm(newStatus === 'approved' ? '¿Aprobar este pago?' : '¿Rechazar este pago?')) return;
    setReviewingId(paymentId);
    try {
      await api.put(`/api/v1/payments/${paymentId}/review`, { status: newStatus });
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al procesar la revisión';
      alert(detail);
    } finally {
      setReviewingId(null);
    }
  };

  if (payments.length === 0) {
    return (
      <p style={{ color: '#888', textAlign: 'center', marginTop: '20px' }}>
        No hay pagos registrados aún.
      </p>
    );
  }

  // Group payments by event title
  const grouped = {};
  payments.forEach((p) => {
    const key = p.title || 'Sin evento';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(p);
  });

  const eventNames = Object.keys(grouped);
  const filteredGroups = filter === 'all' ? eventNames : [filter];

  return (
    <>
      {/* Event filter */}
      {eventNames.length > 1 && (
        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: '#555', fontSize: '0.9rem', fontWeight: 600 }}>Filtrar por evento:</span>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '4px 14px',
              border: 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              fontWeight: filter === 'all' ? 700 : 400,
              backgroundColor: filter === 'all' ? '#C73B33' : '#f0f0f0',
              color: filter === 'all' ? '#fff' : '#333',
              fontSize: '0.85rem',
            }}
          >
            Todos ({payments.length})
          </button>
          {eventNames.map((name) => (
            <button
              key={name}
              onClick={() => setFilter(name)}
              style={{
                padding: '4px 14px',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                fontWeight: filter === name ? 700 : 400,
                backgroundColor: filter === name ? '#C73B33' : '#f0f0f0',
                color: filter === name ? '#fff' : '#333',
                fontSize: '0.85rem',
              }}
            >
              {name} ({grouped[name].length})
            </button>
          ))}
        </div>
      )}

      {filteredGroups.map((eventName) => {
        const eventPayments = grouped[eventName] || [];
        if (eventPayments.length === 0) return null;

        const approved = eventPayments.filter((p) => p.status === 'approved');
        const pendingReview = eventPayments.filter((p) => p.status === 'pending_review').length;
        const total = approved.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

        return (
          <div key={eventName} style={{ marginBottom: '32px' }}>
            {/* Event header */}
            <h6 style={{
              backgroundColor: '#C73B33',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '6px 6px 0 0',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              {eventName}
            </h6>

            {/* Summary */}
            <div
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderTop: 'none',
                padding: '10px 16px',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap',
                fontSize: '0.9rem',
              }}
            >
              <div>
                <span style={{ color: '#555' }}>Aprobados: </span>
                <strong>{approved.length}</strong>
              </div>
              <div>
                <span style={{ color: '#555' }}>Recaudado: </span>
                <strong style={{ color: '#155724' }}>{formatAmount(total)}</strong>
              </div>
              {pendingReview > 0 && (
                <div>
                  <span
                    style={{
                      backgroundColor: '#cce5ff',
                      color: '#004085',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                    }}
                  >
                    {pendingReview} pendiente{pendingReview > 1 ? 's' : ''} de revisión
                  </span>
                </div>
              )}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-hover table-bordered" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>DNI</th>
                    <th>Monto</th>
                    <th>Comentario</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {eventPayments.map((payment) => {
                    const statusStyle = STATUS_STYLES[payment.status] || { backgroundColor: '#e2e3e5', color: '#383d41' };
                    const statusLabel = STATUS_LABELS[payment.status] || payment.status;
                    const comment = formatComment(payment);
                    const commentBg = commentColor(payment);
                    return (
                      <tr key={payment.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(payment.created_at)}</td>
                        <td>{payment.nombre || '—'}</td>
                        <td>{payment.apellido || '—'}</td>
                        <td>{payment.dni || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatAmount(payment.amount)}</td>
                        <td style={{ fontSize: '0.85rem', maxWidth: '220px' }}>
                          {comment ? (
                            <span
                              style={{
                                backgroundColor: commentBg || '#f0f0f0',
                                color: '#333',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '0.82rem',
                                display: 'inline-block',
                              }}
                            >
                              {comment}
                            </span>
                          ) : (
                            <span style={{ color: '#aaa' }}>—</span>
                          )}
                        </td>
                        <td>
                          <span
                            style={{
                              ...statusStyle,
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {statusLabel}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {payment.status === 'pending_review' && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {payment.receipt_url && (
                                <button
                                  className="btn btn-sm"
                                  style={{ backgroundColor: '#17a2b8', color: '#fff', border: 'none', borderRadius: '6px', padding: '3px 8px', fontSize: '0.8rem' }}
                                  onClick={() => setReceiptUrl(payment.receipt_url)}
                                >
                                  Ver
                                </button>
                              )}
                              <button
                                className="btn btn-sm"
                                style={{ backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', padding: '3px 8px', fontSize: '0.8rem' }}
                                disabled={reviewingId === payment.id}
                                onClick={() => handleReview(payment.id, 'approved')}
                              >
                                Aprobar
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', padding: '3px 8px', fontSize: '0.8rem' }}
                                disabled={reviewingId === payment.id}
                                onClick={() => handleReview(payment.id, 'rejected')}
                              >
                                Rechazar
                              </button>
                            </div>
                          )}
                          {payment.status === 'approved' && payment.receipt_url && (
                            <button
                              className="btn btn-sm"
                              style={{ backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', padding: '3px 8px', fontSize: '0.8rem' }}
                              onClick={() => setReceiptUrl(payment.receipt_url)}
                            >
                              Comprobante
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Receipt image modal */}
      {receiptUrl && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 9999,
          }}
          onClick={() => setReceiptUrl(null)}
        >
          <div
            style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '16px',
              maxWidth: '90vw', maxHeight: '90vh', position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setReceiptUrl(null)}
              style={{
                position: 'absolute', top: '8px', right: '12px',
                background: 'none', border: 'none', fontSize: '1.4rem',
                cursor: 'pointer', color: '#666',
              }}
            >
              X
            </button>
            <img
              src={receiptUrl}
              alt="Comprobante de transferencia"
              style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px' }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentsTable;
